import { preloadImages, getUmbracoMediaItemSrc } from '@/helpers/assetHelper';
import config from '@/config.js';

var store = 
{
    state: {
        isLoading: false,
        manifestIsLoaded: false,
        manifestRaw: {},
        selectedChallenge: null,
        selectedCase: null,
        selectedDataLayer: null,

        // NOTE(Mølby): Organised data from manifestRaw
        siteInfo: {},
        denmarkInfo: {},
        europeInfo: {},
        challenges: [],
        dataLayers: [],
        cases: [],
        factsAboutDk: {},
        factsEuSmall: [],
        factsEuBig: [],
        challengesDk: [],
        dataLayersDk: [],
        challengesEU: [],
        dataLayersEU: [],
        casesDk: [],
        casesEU: [],
        globeState: 'normal', // NOTE(Mølby): 'normal', 'autospin' or 'tutorial' (where 'tutorial' is the instruction for how to drag),
        backgroundIsTransitioning: false,
        imagesToLoad: 0,
        imagesLoaded: 0,
    },

    _subscribers: [],

    subscribe(callback)
    {
        this._subscribers.push(callback);
        // NOTE(Mølby): Do initial callback
        callback(this.state);

        // NOTE(Mølby): Return unsubscribe callback
        return ()=>{
            this._subscribers = this._subscribers.filter((existingCallback) => (existingCallback !== callback));
        }
    },

    setBackgroundTransitionState(newState)
    {
        this.state.backgroundIsTransitioning = newState;
        this._updateSubscribers();
    },

    setGlobeState(newState)
    {
        this.state.globeState = newState;
        this._updateSubscribers();
    },

    setSelectedChallenge(challenge)
    {
        this.state.selectedChallenge = challenge;
        this._updateSubscribers();
    },

    setSelectedCase(selectedCase)
    {
        this.state.selectedCase = selectedCase;
        this._updateSubscribers();
    },

    setSelectedDataLayer(dataLayer)
    {
        this.state.selectedDataLayer = dataLayer;
        this._updateSubscribers();
    },

    loadManifest()
    {
        var manifestPath = config.manifestPath;

        // NOTE(Mølby): Init state
        this.state.isLoading = true;
        this.state.manifestIsLoaded = false;
        this.state.manifestRaw = {};
        this._updateSubscribers();

        return fetch(manifestPath, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-store',
            credentials: 'same-origin',
            headers: {
                // 'Content-Type': 'application/json'
            },
        })
        .then(
            (response) => {
                return response.json();
            }
        )
        .then(
            (data) => {
                // NOTE(Mølby): Update state with results
                this.state.manifestRaw = data;
                
                var greenTogetherData = this._sortManifestData(data);

                this.state.siteInfo = greenTogetherData.siteInfo;
                this.state.denmarkInfo = greenTogetherData.denmarkInfo;
                this.state.europeInfo = greenTogetherData.europeInfo;
                this.state.challenges = greenTogetherData.challenges;
                this.state.dataLayers = greenTogetherData.dataLayers;
                this.state.cases = greenTogetherData.cases;
                this.state.factsAboutDk = greenTogetherData.factsAboutDk;
                this.state.factsEuSmall = greenTogetherData.factsEuSmall;
                this.state.factsEuBig = greenTogetherData.factsEuBig;
                this.state.challengesDk = greenTogetherData.challengesDk;
                this.state.dataLayersDk = greenTogetherData.dataLayersDk;
                this.state.challengesEU = greenTogetherData.challengesEU;
                this.state.dataLayersEU = greenTogetherData.dataLayersEU;
                this.state.casesDk = greenTogetherData.casesDk;
                this.state.casesEU = greenTogetherData.casesEU;

                return this._preloadImages()
                    .then(()=>{
                        this.state.isLoading = false;
                        this.state.manifestIsLoaded = true;
                    });
            }
        )
        .then(()=>{
            this._updateSubscribers();
        });
    },
    
    // NOTE(Mølby): Internal methods

    _preloadImages() {
        var imageUrlsToPreload = [];
    
        var casesMerged = this.state.cases.concat(this.state.casesDk);
    
        casesMerged.forEach((caseData) => {
            if (
                caseData.challengesMainMedia &&
                caseData.challengesMainMedia.length &&
                caseData.challengesMainMedia[0]
            ) {
                const mainMedia = caseData.challengesMainMedia[0];
                if (mainMedia.nsAlias === 'caseSlideshowImage' && mainMedia.image) {
                    let imageUrl = getUmbracoMediaItemSrc(mainMedia.image);
                    if (imageUrl) imageUrlsToPreload.push(imageUrl);
                } else if (mainMedia.nsAlias === 'caseSlideshowVideo' && mainMedia.videoImage) {
                    let imageUrl = getUmbracoMediaItemSrc(mainMedia.videoImage);
                    if (imageUrl) imageUrlsToPreload.push(imageUrl);
                }
            }
    
            if (caseData.caseDatachallengesExtraImage) {
                let imageUrl = getUmbracoMediaItemSrc(caseData.caseDatachallengesExtraImage);
                if (imageUrl) imageUrlsToPreload.push(imageUrl);
            }
        });
    
        if (this.state.factsAboutDk.mediaToday && this.state.factsAboutDk.mediaToday.length) {
            const mediaToday = this.state.factsAboutDk.mediaToday[0];
            if (mediaToday.nsAlias === 'caseSlideshowImage' && mediaToday.image) {
                let imageUrl = getUmbracoMediaItemSrc(mediaToday.image);
                if (imageUrl) imageUrlsToPreload.push(imageUrl);
            } else if (mediaToday.nsAlias === 'caseSlideshowVideo' && mediaToday.videoImage) {
                let imageUrl = getUmbracoMediaItemSrc(mediaToday.videoImage);
                if (imageUrl) imageUrlsToPreload.push(imageUrl);
            }
        }
    
        this.state.dataLayers.forEach((dataLayer) => {
            if (dataLayer.dataPoints) {
                dataLayer.dataPoints.forEach((dataPoint) => {
                    if (dataPoint.image) {
                        let imageUrl = getUmbracoMediaItemSrc(dataPoint.image);
                        if (imageUrl) imageUrlsToPreload.push(imageUrl);
                    }
                });
            }
        });
    
        this.state.imagesToLoad = imageUrlsToPreload.length;
        this.state.imagesLoaded = 0;
    
        return preloadImages(imageUrlsToPreload, false, () => {
            this.state.imagesLoaded++;
            this._updateSubscribers();
        });
    },

    _updateSubscribers()
    {
        this._subscribers.forEach((callback)=>{
            callback(this.state);
        });
    },

    _sortManifestData(manifestData)
    {
     
        var sortedData = {
            siteInfo: {},
            denmarkInfo: {},
            europeInfo: {},
            challenges: [],
            dataLayers: [],
            cases: [],
            factsAboutDk: {},
            factsEuSmall: [],
            factsEuBig: [],
            challengesDk: [],
            dataLayersDk: [],
            challengesEU: [],
            dataLayersEU: [],
            casesDk: [],
            casesEU: []
        };

        // NOTE(Mølby): Grab root of green together from manifest custom data
        var greenTogetherRoot = null;
        if (manifestData.custom && manifestData.custom.length > 0 && manifestData.custom[0].type === 'greenTogether')
        {
            greenTogetherRoot = manifestData.custom[0];
        }

        if (greenTogetherRoot)
        {
            // NOTE(Mølby): Get siteinfo from root node
            sortedData.siteInfo = greenTogetherRoot.data;

            greenTogetherRoot.Children.forEach((node)=>{

                // NOTE(Mølby): Get global challenges
                if (node.type === 'globalChallenge')
                {
                    var challengeId = node.id;

                    // NOTE(Mølby): Add ID to the data object
                    node.data.id = challengeId;
                    sortedData.challenges.push(node.data);

                    // NOTE(Mølby): Get cases and data layers
                    if (node.Children)
                    {
                        node.Children.forEach((node)=>{
                            // NOTE(Mølby): Add ID to the data object
                            node.data.id = node.id;
                            node.data.challengeId = challengeId;
                            
                            if (node.type === 'case')
                            {
                                sortedData.cases.push(node.data);
                            }
                            else if (node.type === 'dataLayer')
                            {
                                sortedData.dataLayers.push(node.data);
                            }
                        });
                    }
                }

                else if (node.type === 'denmark')
                {
                    sortedData.denmarkInfo = node.data;

                    node.Children.forEach((node)=>{

                        // NOTE(Mølby): Get DK challenges
                        if (node.type === 'globalChallengeDK')
                        {
                            var challengeId = node.id;

                            // NOTE(Mølby): Add ID to the data object
                            node.data.id = challengeId;
                            sortedData.challengesDk.push(node.data);

                            // NOTE(Mølby): Get cases and data layers
                            if (node.Children)
                            {
                                node.Children.forEach((node)=>{
                                    // NOTE(Mølby): Add ID to the data object
                                    node.data.id = node.id;
                                    node.data.challengeId = challengeId;
                                    
                                    if (node.type === 'case')
                                    {
                                        sortedData.casesDk.push(node.data);
                                    }
                                    else if (node.type === 'dataLayerDk')
                                    {
                                        sortedData.dataLayersDk.push(node.data);
                                    }
                                });
                            }
                        }

                        // NOTE(Mølby): Get facts about DK
                        else if (node.type === 'factsAboutDK')
                        {
                            sortedData.factsAboutDk = node.data;
                        }
                    });
                }
                else if (node.type === 'Europe')
                    {
                        sortedData.europeInfo = node.data;
    
                        node.Children.forEach((node)=>{
    
                            // NOTE(Mølby): Get DK challenges
                            if (node.type === 'globalChallengeEU')
                            {
                                var challengeId = node.id;
    
                                // NOTE(Mølby): Add ID to the data object
                                node.data.id = challengeId;
                                sortedData.challengesEU.push(node.data);
    
                                // NOTE(Mølby): Get cases and data layers
                                if (node.Children)
                                {
                                    node.Children.forEach((node)=>{
                                        // NOTE(Mølby): Add ID to the data object
                                        node.data.id = node.id;
                                        node.data.challengeId = challengeId;
                                        
                                        if (node.type === 'caseEU')
                                        {
                                            sortedData.casesEU.push(node.data);
                                        }
                                        else if (node.type === 'dataLayerEU')
                                        {
                                            sortedData.dataLayersEU.push(node.data);
                                        }
                                        else if (node.type === 'factsEuSmall')
                                        {
                                            sortedData.factsEuSmall.push(node.data);
                                        }
                                        else if (node.type === 'factsEuBig')
                                            {
                                                    sortedData.factsEuBig.push(node.data);
                                            }
                                    });
                                }
                            }
    
                        });
                    }
            });
        }

        return sortedData;
    },

    _delayPromise(durationMs)
    {
        return new Promise((resolve/*, reject*/)=>{
            setTimeout(() => {
                resolve();
            }, durationMs);
        });
    }
}

export default store;